import { ApiProblemError, reconciliationKeys } from '@taler/api-client'
import type { LocalDate } from '@taler/domain'
import { useQuery, useQueryClient } from '@tanstack/vue-query'
import { computed, type ComputedRef, ref } from 'vue'
import { useApi } from '@/app/api'

export interface UseOpenReconciliation {
  /** Undefined while loading, or once it's known no reconciliation is open. */
  readonly summary: ComputedRef<{ id: string; accountsChecked: number } | undefined>
  readonly isLoading: ComputedRef<boolean>
  readonly isStarting: ComputedRef<boolean>
  /** Creates a new session, or resolves to the one already open for this ledger. */
  start(observedDate: LocalDate): Promise<string>
}

/** Cheap presence check for the ledger's one shared open reconciliation, via the list's first page. */
export function useOpenReconciliation(options: {
  readonly ledgerId: () => string | undefined
  readonly enabled: () => boolean
}): UseOpenReconciliation {
  const api = useApi()
  const queryClient = useQueryClient()
  const active = computed(() => options.enabled() && options.ledgerId() !== undefined)

  const query = useQuery({
    queryKey: computed(() => reconciliationKeys.openCheck(options.ledgerId() ?? '')),
    queryFn: ({ signal }) => api.reconciliations.list(options.ledgerId()!, { limit: 1 }, signal),
    enabled: active,
    retry: false,
  })

  const isStarting = ref(false)

  return {
    summary: computed(() => {
      const first = query.data.value?.items[0]
      return first !== undefined && first.status === 'OPEN'
        ? { id: first.id, accountsChecked: first.accountsChecked }
        : undefined
    }),
    isLoading: computed(() => active.value && query.isPending.value),
    isStarting: computed(() => isStarting.value),
    async start(observedDate) {
      const ledgerId = options.ledgerId()
      if (ledgerId === undefined) throw new Error('No ledger selected')
      isStarting.value = true
      try {
        const created = await api.reconciliations.create(ledgerId, observedDate)
        await Promise.all(
          reconciliationKeys
            .invalidatedByWrite(ledgerId)
            .map((key) => queryClient.invalidateQueries({ queryKey: key })),
        )
        return created.id
      } catch (error) {
        // Two tabs racing "Reconcile" both land on the one the winner just opened.
        const openId = error instanceof ApiProblemError ? error.property<string>('id') : undefined
        if (openId !== undefined) return openId
        throw error
      } finally {
        isStarting.value = false
      }
    },
  }
}
